import { useEffect, useMemo, useState, useCallback } from "react";
import { api, API } from "@/lib/api";

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

// ── Date preset helpers (local time → YYYY-MM-DD) ─────────────────────────
function ymd(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}
function preset(days) {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - (days - 1));
  return { start: ymd(start), end: ymd(end) };
}

const PRESETS = [
  { id: "today", label: "Today", get: () => preset(1) },
  { id: "7d", label: "Last 7 days", get: () => preset(7) },
  { id: "30d", label: "Last 30 days", get: () => preset(30) },
  { id: "all", label: "All time", get: () => ({ start: "", end: "" }) },
];

// ── Sortable column header ────────────────────────────────────────────────
function SortHeader({ label, field, sortBy, sortDir, onSort, align = "left", className = "" }) {
  const active = sortBy === field;
  const dir = active ? sortDir : null;
  return (
    <th
      onClick={() => onSort(field)}
      data-testid={`sort-${field}`}
      className={`px-3 py-2 text-xs font-medium text-zinc-400 select-none cursor-pointer hover:text-emerald-300 ${
        align === "right" ? "text-right" : "text-left"
      } ${className}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <span className="text-[10px] text-zinc-600">
          {dir === "asc" ? "▲" : dir === "desc" ? "▼" : "↕"}
        </span>
      </span>
    </th>
  );
}

// ── Retention policy panel (admin-only) ───────────────────────────────────
function RetentionPanel({ onPurge }) {
  const [pol, setPol] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/history/retention");
      setPol(data);
    } catch { setPol({ enabled: false, days: 90, last_run_at: null, last_run_deleted: 0 }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!pol) return null;

  const save = async (patch) => {
    setSaving(true);
    try {
      const next = { enabled: pol.enabled, days: pol.days, ...patch };
      const { data } = await api.put("/admin/history/retention", next);
      setPol((p) => ({ ...p, ...data }));
    } finally { setSaving(false); }
  };

  const runNow = async () => {
    if (!confirm(`Run retention sweep now?\n\nThis will delete history older than ${pol.days} days across ALL users.`)) return;
    const { data } = await api.post("/admin/history/retention/run-now");
    alert(`Sweep complete: ${data.deleted} rows deleted.`);
    load();
    onPurge && onPurge();
  };

  const deleteAllNow = async () => {
    const daysStr = prompt("Delete history rows older than how many days, across ALL users?\n\nLeave blank to delete EVERYTHING.", "30");
    if (daysStr === null) return;
    const trimmed = (daysStr || "").trim();
    const path = trimmed === ""
      ? "/admin/history"
      : `/admin/history?older_than_days=${parseInt(trimmed, 10)}`;
    if (trimmed === "" && !confirm("Type EXACTLY YES to wipe ALL history across ALL users:")) return;
    const { data } = await api.delete(path);
    alert(`Deleted ${data.deleted} rows.`);
    onPurge && onPurge();
  };

  return (
    <div className="rounded-lg border border-zinc-800 bg-[#0e0f14] p-4 mb-5" data-testid="retention-panel">
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <div className="text-xs tracking-widest text-zinc-500 uppercase">Auto-delete history</div>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            data-testid="retention-toggle"
            checked={!!pol.enabled}
            disabled={saving}
            onChange={(e) => save({ enabled: e.target.checked })}
            className="accent-emerald-500"
          />
          <span className="text-zinc-300">{pol.enabled ? "Enabled" : "Disabled"}</span>
        </label>
        <div className="flex items-center gap-2">
          <span className="text-zinc-500 text-xs">older than</span>
          <input
            type="number"
            min="1"
            max="3650"
            data-testid="retention-days"
            value={pol.days}
            onChange={(e) => setPol((p) => ({ ...p, days: parseInt(e.target.value || "0", 10) }))}
            onBlur={() => save({ days: Math.max(1, Math.min(pol.days, 3650)) })}
            className="w-20 px-2 py-1 rounded bg-zinc-900 border border-zinc-800 text-zinc-100 text-sm focus:border-emerald-700 outline-none"
          />
          <span className="text-zinc-500 text-xs">days</span>
        </div>
        <button
          data-testid="retention-run-now"
          onClick={runNow}
          className="text-xs px-3 py-1.5 rounded border border-emerald-700/60 bg-emerald-700/10 text-emerald-300 hover:bg-emerald-700/20"
        >Run sweep now</button>
        <button
          data-testid="delete-history-all"
          onClick={deleteAllNow}
          className="text-xs px-3 py-1.5 rounded border border-red-900/60 text-red-300 hover:bg-red-900/20"
        >Delete history…</button>
        <div className="flex-1" />
        <div className="text-[11px] text-zinc-500">
          {pol.last_run_at
            ? `Last run: ${new Date(pol.last_run_at).toLocaleString()} · ${pol.last_run_deleted} rows deleted`
            : "Never run yet"}
        </div>
      </div>
      {pol.enabled && (
        <div className="text-[11px] text-zinc-600 mt-2">
          The browser will automatically delete URL history older than <b className="text-zinc-400">{pol.days}</b> days.
          Sweep runs every 6 hours in the background.
        </div>
      )}
    </div>
  );
}

export default function History() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [activePreset, setActivePreset] = useState("all");
  const [search, setSearch] = useState("");
  const [domain, setDomain] = useState("");
  const [blockedOnly, setBlockedOnly] = useState(false);

  const [sortBy, setSortBy] = useState("ts");
  const [sortDir, setSortDir] = useState("desc");

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Load users once
  useEffect(() => {
    (async () => {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    })();
  }, []);

  const queryParams = useMemo(() => {
    const p = { limit: pageSize, skip: page * pageSize, sort_by: sortBy, sort_dir: sortDir };
    if (userId) p.user_id = userId;
    if (start) p.start_date = start;
    if (end) p.end_date = end;
    if (search) p.search = search;
    if (domain) p.domain = domain;
    if (blockedOnly) p.blocked_only = true;
    return p;
  }, [pageSize, page, sortBy, sortDir, userId, start, end, search, domain, blockedOnly]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/history", { params: queryParams });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => { load(); }, [load]);

  // Reset page when any filter changes
  useEffect(() => { setPage(0); }, [userId, start, end, search, domain, blockedOnly, pageSize]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const onSort = (field) => {
    if (sortBy === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortDir(field === "ts" ? "desc" : "asc");
    }
  };

  const applyPreset = (id) => {
    setActivePreset(id);
    const { start: s, end: e } = PRESETS.find((p) => p.id === id).get();
    setStart(s); setEnd(e);
  };

  const clearFilters = () => {
    setUserId(""); setStart(""); setEnd(""); setSearch(""); setDomain("");
    setBlockedOnly(false); setActivePreset("all");
    setSortBy("ts"); setSortDir("desc");
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem("snd_token");
      const qs = new URLSearchParams();
      Object.entries(queryParams).forEach(([k, v]) => {
        if (k === "limit" || k === "skip") return;
        if (v !== undefined && v !== null && v !== "") qs.append(k, v);
      });
      const res = await fetch(`${API}/admin/history/export.csv?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.href = url; a.download = `url-history-${stamp}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const hasFilters = userId || start || end || search || domain || blockedOnly;

  return (
    <div className="p-8 max-w-[1400px] mx-auto" data-testid="history-page">
      <div className="mb-6">
        <div className="text-[11px] tracking-[0.18em] text-emerald-400/80">URL HISTORY</div>
        <h1 className="text-2xl font-semibold mt-1">All activity</h1>
        <p className="text-xs text-zinc-500 mt-1">
          {loading ? "Loading…" : `${total.toLocaleString()} ${total === 1 ? "entry" : "entries"}`}
          {hasFilters ? " (filtered)" : ""}
        </p>
      </div>

      <RetentionPanel onPurge={() => { load(); }} />

      {/* Sticky filter bar */}
      <div
        className="sticky top-0 z-20 -mx-2 px-2 py-3 mb-4 backdrop-blur-md bg-[#0b0c10]/85 border-b border-zinc-800"
        data-testid="filter-bar"
      >
        {/* Row 1: Presets + Export */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="text-[11px] uppercase tracking-wider text-zinc-500 mr-1">Range:</span>
          {PRESETS.map((p) => (
            <button
              key={p.id}
              data-testid={`preset-${p.id}`}
              onClick={() => applyPreset(p.id)}
              className={`px-3 py-1.5 text-xs rounded-full border transition ${
                activePreset === p.id
                  ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-200"
                  : "border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-zinc-200"
              }`}
            >
              {p.label}
            </button>
          ))}
          <div className="flex-1" />
          <button
            data-testid="clear-filters"
            onClick={clearFilters}
            disabled={!hasFilters}
            className="px-3 py-1.5 text-xs rounded-md border border-zinc-800 hover:border-zinc-600 text-zinc-300 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            Clear
          </button>
          <button
            data-testid="export-csv"
            onClick={exportCsv}
            disabled={exporting || total === 0}
            className="px-3 py-1.5 text-xs rounded-md border border-emerald-700/60 bg-emerald-700/10 text-emerald-300 hover:bg-emerald-700/20 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {exporting ? "Exporting…" : "Export CSV"}
          </button>
        </div>

        {/* Row 2: Detailed filters */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-3">
            <label className="text-[10px] uppercase tracking-wider text-zinc-500">User</label>
            <select
              data-testid="user-filter"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              className="w-full mt-1 px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-sm focus:border-emerald-700 outline-none"
            >
              <option value="">All users</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.email}{u.name ? ` — ${u.name}` : ""}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-wider text-zinc-500">From</label>
            <input
              data-testid="start-date"
              type="date"
              value={start}
              onChange={(e) => { setStart(e.target.value); setActivePreset("custom"); }}
              className="w-full mt-1 px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-sm focus:border-emerald-700 outline-none"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-wider text-zinc-500">To</label>
            <input
              data-testid="end-date"
              type="date"
              value={end}
              onChange={(e) => { setEnd(e.target.value); setActivePreset("custom"); }}
              className="w-full mt-1 px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-sm focus:border-emerald-700 outline-none"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-wider text-zinc-500">Domain</label>
            <input
              data-testid="domain-filter"
              placeholder="e.g. google.com"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              className="w-full mt-1 px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-sm focus:border-emerald-700 outline-none"
            />
          </div>
          <div className="md:col-span-3">
            <label className="text-[10px] uppercase tracking-wider text-zinc-500">Search</label>
            <input
              data-testid="history-filter"
              placeholder="Title or URL contains…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full mt-1 px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-sm focus:border-emerald-700 outline-none"
            />
          </div>
          <div className="md:col-span-12 flex items-center gap-2">
            <label className="inline-flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
              <input
                data-testid="blocked-only"
                type="checkbox"
                checked={blockedOnly}
                onChange={(e) => setBlockedOnly(e.target.checked)}
                className="accent-rose-500"
              />
              Blocked attempts only
            </label>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-zinc-800 bg-[#0e0f14] overflow-hidden">
        <table className="w-full text-sm table-fixed">
          <colgroup>
            <col className="w-[200px]" />
            <col />
            <col className="w-[240px]" />
            <col className="w-[160px]" />
          </colgroup>
          <thead className="bg-zinc-900/60 border-b border-zinc-800">
            <tr>
              <SortHeader label="User" field="user_email" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <SortHeader label="URL" field="url" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <SortHeader label="Title" field="title" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <SortHeader label="Time" field="ts" sortBy={sortBy} sortDir={sortDir} onSort={onSort} align="right" />
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-900">
            {loading && (
              <tr><td colSpan={4} className="p-8 text-center text-xs text-zinc-500">Loading…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={4} className="p-8 text-center text-xs text-zinc-500">No matching activity.</td></tr>
            )}
            {!loading && items.map((h) => (
              <tr key={h.id} className="hover:bg-zinc-900/40">
                <td className="px-3 py-2.5 text-xs text-emerald-300 truncate">{h.user_email}</td>
                <td className="px-3 py-2.5 text-zinc-200 min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    {h.blocked && (
                      <span className="shrink-0 px-1.5 py-0.5 text-[10px] rounded bg-rose-500/15 text-rose-300 border border-rose-500/30">
                        BLOCKED
                      </span>
                    )}
                    <a
                      href={h.url}
                      target="_blank"
                      rel="noreferrer"
                      title={h.url}
                      className="truncate block hover:text-emerald-300 min-w-0"
                    >
                      {h.url}
                    </a>
                  </div>
                </td>
                <td className="px-3 py-2.5 text-zinc-400 truncate" title={h.title || ""}>{h.title || "—"}</td>
                <td className="px-3 py-2.5 text-right text-[11px] text-zinc-500">
                  {new Date(h.ts).toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination — always visible */}
      <div className="flex flex-wrap items-center justify-between mt-4 gap-3 text-xs" data-testid="pager">
        <div className="flex items-center gap-2 text-zinc-500">
          <span>Rows per page:</span>
          <select
            data-testid="page-size"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="px-2 py-1 rounded-md bg-zinc-900 border border-zinc-800 text-zinc-300"
          >
            {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <span className="ml-3">
            {total === 0
              ? "0 of 0"
              : `${page * pageSize + 1}–${Math.min((page + 1) * pageSize, total)} of ${total.toLocaleString()}`}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            data-testid="page-first"
            onClick={() => setPage(0)}
            disabled={page === 0}
            className="px-2.5 py-1 rounded-md border border-zinc-800 hover:border-zinc-600 disabled:opacity-30 disabled:cursor-not-allowed"
          >« First</button>
          <button
            data-testid="page-prev"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className="px-2.5 py-1 rounded-md border border-zinc-800 hover:border-zinc-600 disabled:opacity-30 disabled:cursor-not-allowed"
          >‹ Prev</button>
          <span className="px-2 text-zinc-500">Page {page + 1} / {totalPages}</span>
          <button
            data-testid="page-next"
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            className="px-2.5 py-1 rounded-md border border-zinc-800 hover:border-zinc-600 disabled:opacity-30 disabled:cursor-not-allowed"
          >Next ›</button>
          <button
            data-testid="page-last"
            onClick={() => setPage(totalPages - 1)}
            disabled={page >= totalPages - 1}
            className="px-2.5 py-1 rounded-md border border-zinc-800 hover:border-zinc-600 disabled:opacity-30 disabled:cursor-not-allowed"
          >Last »</button>
        </div>
      </div>
    </div>
  );
}
